import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.js';

/** Page heading row: title, supporting line, right-aligned actions. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h1 className="text-[var(--text-xl)] font-semibold tracking-[-0.01em] text-[var(--text)]">
          {title}
        </h1>
        {description && (
          <p className="mt-0.5 max-w-2xl text-[var(--text-base)] text-[var(--text-2)]">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={cx(
        'rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-1)]',
        padded && 'p-4',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('mb-3 flex items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="text-[var(--text-md)] font-semibold text-[var(--text)]">{title}</h2>
        {description && (
          <p className="mt-0.5 text-[var(--text-base)] text-[var(--text-2)]">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Forms are readable, not full-bleed: one column capped at ~640px. */
export function FormLayout({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('flex max-w-[640px] flex-col gap-4', className)}>{children}</div>;
}

/** Tables and boards are full-bleed. */
export function FullBleed({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('w-full min-w-0', className)}>{children}</div>;
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'flex flex-wrap items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)]',
        'bg-[var(--surface)] px-2.5 py-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Key/value grid used by detail panels. */
export function DefinitionList({
  items,
  className,
}: {
  items: { label: string; value: ReactNode }[];
  className?: string;
}) {
  return (
    <dl className={cx('grid grid-cols-[minmax(96px,auto)_1fr] gap-x-4 gap-y-2', className)}>
      {items.map(({ label, value }) => (
        <div key={label} className="contents">
          <dt className="text-[var(--text-base)] text-[var(--text-3)]">{label}</dt>
          <dd className="min-w-0 break-words text-[var(--text-base)] text-[var(--text)]">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: ReactNode; title?: string }[];
  label: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(
        'inline-flex items-center gap-0.5 rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-[var(--surface-2)] p-0.5',
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={cx(
            'rounded-[calc(var(--radius-sm)-2px)] px-2 py-0.5 text-[var(--text-sm)] font-medium',
            'transition-colors duration-[var(--dur-fast)]',
            value === option.value
              ? 'bg-[var(--surface)] text-[var(--text)] shadow-[var(--shadow-1)]'
              : 'text-[var(--text-2)] hover:text-[var(--text)]',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  label,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  tabs: { value: T; label: ReactNode; count?: number }[];
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx('flex items-center gap-1 border-b border-[var(--border)]', className)}
    >
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={value === tab.value}
          onClick={() => onChange(tab.value)}
          className={cx(
            '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[var(--text-base)] font-medium',
            'transition-colors duration-[var(--dur-fast)]',
            value === tab.value
              ? 'border-[var(--accent)] text-[var(--text)]'
              : 'border-transparent text-[var(--text-2)] hover:text-[var(--text)]',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="tnum rounded-[var(--radius-pill)] bg-[var(--surface-2)] px-1.5 text-[var(--text-sm)] text-[var(--text-2)]">
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
